import React, {useEffect, useState} from "react";
import {
    Box,
    Button,
    Divider,
    Flex,
    FormControl,
    FormLabel,
    Input,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Tag,
    TagLabel,
    Text,
    useToast,
    VStack,
} from "components-lib";
import {API_BASE_URL, axiosInstance} from "config/api";
import FileUploadField from "../../../../components/FileUploadField";
import Bill, {generateBillPdf, uploadFile} from "./Bill";
import {renderBillToBlob} from "../utils/billPdfHelper";

// Payment states enum (same as BookAppointmentModal)
const PaymentStates = {
    PENDING: "PENDING",
    PAID: "PAID",
    REFUND_PENDING: "REFUND-PENDING",
    REFUND_DONE: "REFUNDED",
};

const backendBase = API_BASE_URL.replace(/\/+$/g, "");
const billsBaseUrl = `${backendBase}/api/bills`;

const PaymentReceiptModal = ({
                                 isOpen,
                                 onClose,
                                 onPaymentConfirm,
                                 patientName,
                                 appointmentId,
                             }) => {
    const [error, setError] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [existingBill, setExistingBill] = useState(null);
    const [isRefundCase, setIsRefundCase] = useState(false);
    const [isBillViewOpen, setIsBillViewOpen] = useState(false);
    const [receiptItems, setReceiptItems] = useState([]);
    const [billPaymentNowSelection, setBillPaymentNowSelection] = useState(null);
    const [paymentState, setPaymentState] = useState(null);
    const [ispaid, setispaid] = useState(false);
    const [ispending, setispending] = useState(false);
    const [billSummary, setBillSummary] = useState({
        grossAmount: 0,
        subtotal: 0,
        discountAmount: 0,
        taxAmount: 0,
        taxPercentage: 0,
        netAmount: 0,
        RecivedAmt: 0, // previously received amount
        PendingAmt: 0, // pending before applying additionalReceived
    });

    // additional amount received now (entered by user) — separate from previously received amount
    const [additionalReceived, setAdditionalReceived] = useState(0);

    const toast = useToast();

    useEffect(() => {
        if (isOpen && appointmentId) {
            fetchBillDetails();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, appointmentId]);

    const fetchBillDetails = async () => {
        try {
            const bill = await fetchExistingBill(appointmentId);
            if (bill) {
                setExistingBill(bill);
                // Check if it's a refund case
                const isPendingRefund =
                    bill.payment_action === PaymentStates.REFUND_PENDING &&
                    parseFloat(bill.pending_amt) < 0;
                setIsRefundCase(isPendingRefund);

                // normalize numeric fields to numbers
                const totalAmt = parseFloat(bill.total_amt) || 0;
                const receivedAmt = parseFloat(bill.received_amt) || 0;
                const pendingAmt = parseFloat(bill.pending_amt);
                const computedPending = !isNaN(pendingAmt) ? pendingAmt : totalAmt - receivedAmt;

                setBillSummary({
                    grossAmount: totalAmt,
                    subtotal: totalAmt,
                    discountAmount: 0,
                    taxAmount: Number(bill.tax_amount ?? bill.taxAmount ?? 0),
                    taxPercentage: Number(bill.tax_percentage ?? bill.taxPercentage ?? 0),
                    netAmount: totalAmt,
                    RecivedAmt: receivedAmt, // previously received
                    PendingAmt: computedPending,
                });

                // auto-fill the additional received input with pending amount (so user can quickly accept full payment)
                setAdditionalReceived(Number(computedPending) || 0);

                setPaymentState(bill.payment_action || PaymentStates.PENDING);

                // If it's a refund case, adjust the UI accordingly
                if (isPendingRefund) {
                    setispending(false);
                    setispaid(false);
                } else {
                    // set paid/pending flags based on pending amt
                    const pAmt = !isNaN(parseFloat(bill.pending_amt)) ? parseFloat(bill.pending_amt) : (totalAmt - receivedAmt);
                    setispaid(pAmt === 0);
                    setispending(pAmt > 0);
                }
            }
        } catch (err) {
            toast({
                title: "Error",
                description:
                    "Failed to fetch bill details: " + (err.message || "Unknown error"),
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        }
    };

    const fetchExistingBill = async (appointmentId) => {
        if (!appointmentId) return null;
        try {
            const token =
                localStorage.getItem("token") || localStorage.getItem("Token");
            const response = await axiosInstance.get("api/bills/getAll", {
                headers: {Authorization: token ? `Bearer ${token}` : ""},
                params: {appointment_id: appointmentId},
            });
            const bills =
                response?.data?.bills || response?.data?.data || response?.data || [];
            const billRecord = Array.isArray(bills) ? bills[0] : bills;
            return billRecord || null;
        } catch (err) {
            console.error("Failed to fetch bill for appointment", err);
            throw err;
        }
    };

    const uploadFilesAndSetUrls = async (files = [], appendText = "") => {
        if (!files || files.length === 0) return [];
        try {
            const results = await Promise.all(
                files.map(async (file) => {
                    try {
                        const url = await uploadFile(file, {appendText});
                        return {name: `${appendText}${file.name}`, url};
                    } catch (err) {
                        console.warn("Single file upload failed:", err);
                        return null;
                    }
                }),
            );

            const ok = results.filter(Boolean);
            setReceiptItems((prev) => {
                const merged = [...(Array.isArray(prev) ? prev : []), ...ok];
                const seen = new Set();
                return merged.filter((it) => {
                    const key = it.url || it.name;
                    if (!key || seen.has(key)) return false;
                    seen.add(key);
                    return true;
                });
            });
            return ok.map((r) => r.url).filter(Boolean);
        } catch (error) {
            console.error("Upload failed:", error);
            toast({
                title: "Upload Failed",
                description: error.message || "Failed to upload files",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
            return [];
        }
    };

    const removeReceiptItem = (index) => {
        setReceiptItems((prev) => {
            const next = Array.isArray(prev) ? prev.slice() : [];
            next.splice(index, 1);
            return next;
        });
    };

    const handleConfirmPayment = async () => {
        if (!receiptItems.length) {
            setError("Please upload at least one payment receipt");
            return;
        }
        setError("");

        // Validation: for non-refund cases, ensure additionalReceived <= pendingBefore and prev+additional <= total
        if (!isRefundCase) {
            const net = Number(billSummary.netAmount) || 0;
            const prevRec = Number(billSummary.RecivedAmt) || 0;
            const add = Number(additionalReceived) || 0;
            const pendingBefore = Number(billSummary.PendingAmt) || (net - prevRec);

            if (add < 0 || add > pendingBefore) {
                setError("Amount received now must be between 0 and the pending amount");
                return;
            }

            const newTotalReceived = prevRec + add;
            if (newTotalReceived < 0 || newTotalReceived > net) {
                setError("Total received after this payment must be between 0 and total amount");
                return;
            }

            // Allow small epsilon
            const pendingAfter = parseFloat((net - newTotalReceived).toFixed(2));
            if (Math.abs(newTotalReceived + pendingAfter - net) > 0.01) {
                setError("Received amount plus pending amount must equal the total amount");
                return;
            }
        }

        setIsSubmitting(true);

        try {
            // compute amounts to be used for PDF and API updates
            const prevRec = Number(billSummary.RecivedAmt) || 0;
            const add = Number(additionalReceived) || 0;
            const net = Number(billSummary.netAmount) || 0;
            // For refunds, additionalReceived is expected to be 0; final received remains prevRec
            const newTotalReceived = isRefundCase ? prevRec : prevRec + add;
            const pendingAfter = isRefundCase ? 0 : parseFloat((net - newTotalReceived).toFixed(2));
            const displayedReceived = parseFloat((prevRec + Number(additionalReceived || 0)).toFixed(2));

            // Generate bill PDF from the UPDATED bill data. Use the offscreen renderer
            // so we can render a Bill component with the final amounts/status and
            // guarantee the PDF reflects the updated bill.
            // Note: renderBillToBlob is now statically imported at the top of the file

            // Prepare appointment and billSummary data reflecting the update
            const appointmentDataForPdf = {
                patientName: patientName,
                patientId: existingBill?.patient_id,
                doctorId: existingBill?.doctor_id,
                appointmentDate: existingBill?.appointment_date,
                appointmentTime: existingBill?.appointment_time,
            };

            const pdfBillSummary = {
                ...billSummary,
                // RecivedAmt should reflect displayed received amount (prev + additional)
                RecivedAmt: displayedReceived,
                // PendingAmt should be pendingAfter (0 for refunds)
                PendingAmt: pendingAfter,
            };

            const pdfPaymentState = isRefundCase ? PaymentStates.REFUND_DONE : (paymentState || PaymentStates.PENDING);

            const pdfBlob = await renderBillToBlob({
                appointmentData: appointmentDataForPdf,
                billSummary: pdfBillSummary,
                paymentState: pdfPaymentState,
                paidAmount: displayedReceived,
                pendingAmount: pendingAfter,
                refundAmount: isRefundCase ? Math.abs(billSummary.PendingAmt) : 0,
                fileName: `${isRefundCase ? "refund" : "payment"}_bill_${appointmentId}.pdf`,
            });

            if (!pdfBlob) throw new Error("Failed to generate bill PDF");

            // Upload PDF using uploadFile helper
            const pdfUrl = await uploadFile(pdfBlob, {
                fileName: `bill_${Date.now()}.pdf`,
                mimeType: "application/pdf",
            });

            // Get receipt URLs
            const receiptUrls = receiptItems.map((item) => item.url).filter(Boolean);

            // Update bill with receipt and PDF
            const payload = {
                bill_id: existingBill?.id,
                payment_receipt_url: receiptUrls,
                bill_invoice_url: pdfUrl,
                // bill_description: isRefundCase ? "REFUND DONE" : undefined,
            };

            await axiosInstance.post(`${billsBaseUrl}/uploadReceipt`, payload);

            // use previously computed amounts (newTotalReceived, pendingAfter)

            // Update payment status
            await axiosInstance.post("/api/appointment/updatePaymentStatus", {
                appointment_id: appointmentId,
                payment_action: isRefundCase
                    ? PaymentStates.REFUND_DONE
                    : newTotalReceived === net
                        ? PaymentStates.PAID
                        : PaymentStates.PENDING,
                paid_amount: newTotalReceived,
                pending_amount: pendingAfter,
            });

            // For normal (non-refund) payments, update the Bill record with
            // new received/pending amounts so the canonical bill stored on the API
            // matches the appointment state and the uploaded invoice.
            if (!isRefundCase && existingBill && existingBill.id) {
                try {
                    const authToken =
                        localStorage.getItem("token") || localStorage.getItem("Token") || "";

                    const billPaymentAction =
                        newTotalReceived === net ? PaymentStates.PAID : PaymentStates.PENDING;

                    // Build a fuller payload for the bills update endpoint using
                    // values from the previously fetched `existingBill` and the
                    // newly computed amounts. This ensures the API receives the
                    // canonical bill fields (services, total, description, invoice)
                    // along with updated received/pending amounts and the action.
                    const billUpdatePayload = {
                        consultation_type: existingBill.consultation_type || billSummary.consultation_type || "in_clinic",
                        total_amt: Number(existingBill.total_amt ?? billSummary.netAmount ?? net).toFixed(2),
                        services: Array.isArray(existingBill.services) ? existingBill.services : (existingBill.services || []),
                        bill_description: existingBill.bill_description || billSummary.bill_description || "",
                        bill_invoice: pdfUrl || existingBill.bill_invoice || "",
                        tax_amount: Number(existingBill.tax_amount ?? billSummary.taxAmount ?? 0).toFixed(2),
                        tax_percentage: Number(existingBill.tax_percentage ?? existingBill.taxPercentage ?? billSummary.taxPercentage ?? billSummary.tax_percent ?? 10),
                        received_amt: Number(newTotalReceived).toFixed(2),
                        pending_amt: Number(pendingAfter).toFixed(2),
                        payment_action: billPaymentAction,
                    };

                    await axiosInstance.put(
                        `${billsBaseUrl}/update/${existingBill.id}`,
                        billUpdatePayload,
                        {
                            headers: {
                                "Content-Type": "application/json",
                                Authorization: `Bearer ${authToken}`,
                            },
                        },
                    );

                    setExistingBill((prev) => ({
                        ...(prev || {}),
                        received_amt: billUpdatePayload.received_amt,
                        pending_amt: billUpdatePayload.pending_amt,
                        payment_action: billUpdatePayload.payment_action,
                        bill_invoice: billUpdatePayload.bill_invoice,
                    }));
                } catch (err) {
                    console.warn("Failed to update bill record after payment:", err);
                    // non-fatal; continue but surface a toast so user can retry/refresh
                    toast({
                        title: "Partial Success",
                        description:
                            "Payment recorded but failed to update bill record. Please refresh to confirm.",
                        status: "warning",
                        duration: 5000,
                        isClosable: true,
                    });
                }
            }

            // If this was a refund case, also update the Bill record so the
            // bill's pending amount becomes 0 and its payment_action/status is REFUNDED.
            // This makes sure generated PDFs and bill views reflect the refund completion.
            if (isRefundCase && existingBill && existingBill.id) {
                try {
                    const authToken =
                        localStorage.getItem("token") || localStorage.getItem("Token") || "";

                    // Build minimal payload to update the bill record. Keep other fields intact where possible.
                    const billUpdatePayload = {
                        // keep canonical fields from existing bill where available
                        consultation_type: existingBill.consultation_type || billSummary.consultation_type || "in_clinic",
                        total_amt: Number(existingBill.total_amt ?? billSummary.netAmount ?? net).toFixed(2),
                        services: Array.isArray(existingBill.services) ? existingBill.services : (existingBill.services || []),
                        bill_description: (existingBill.bill_description || "") + " - Refund processed",
                        // set pending to 0 and mark as refunded
                        pending_amt: "0.00",
                        payment_action: PaymentStates.REFUND_DONE,
                        // keep/set received amount (backend may expect string)
                        received_amt: existingBill.received_amt || "0.00",
                        // attach the generated invoice url (if present)
                        bill_invoice: pdfUrl || existingBill.bill_invoice || "",
                        tax_amount: Number(existingBill.tax_amount ?? billSummary.taxAmount ?? 0).toFixed(2),
                        tax_percentage: Number(existingBill.tax_percentage ?? existingBill.taxPercentage ?? billSummary.taxPercentage ?? billSummary.tax_percent ?? 10),
                    };

                    // Use same bills update endpoint used elsewhere in the repo
                    await axiosInstance.put(
                        `${billsBaseUrl}/update/${existingBill.id}`,
                        billUpdatePayload,
                        {
                            headers: {
                                "Content-Type": "application/json",
                                Authorization: `Bearer ${authToken}`,
                            },
                        },
                    );

                    // Update local state so UI reflects changes immediately
                    setExistingBill((prev) => ({
                        ...(prev || {}),
                        pending_amt: "0.00",
                        payment_action: PaymentStates.REFUND_DONE,
                        received_amt: billUpdatePayload.received_amt,
                        bill_invoice: billUpdatePayload.bill_invoice,
                    }));
                } catch (err) {
                    console.warn("Failed to update bill record after refund:", err);
                    // non-fatal — show a toast but continue
                    // toast({
                    //   title: "Partial Success",
                    //   description:
                    //     "Refund processed but failed to update bill record. Please refresh to confirm.",
                    //   status: "warning",
                    //   duration: 5000,
                    //   isClosable: true,
                    // });
                }
            }

            toast({
                title: isRefundCase ? "Refund Completed" : "Payment Updated",
                description: isRefundCase
                    ? "Refund has been processed and receipt uploaded successfully"
                    : "Payment has been confirmed and receipt uploaded successfully",
                status: "success",
                duration: 3000,
                isClosable: true,
            });

            // Inform parent with the receipt URL and the resulting payment status
            const resultingPaymentStatus = isRefundCase
                ? PaymentStates.REFUND_DONE
                : (newTotalReceived === net ? PaymentStates.PAID : PaymentStates.PENDING);

            if (typeof onPaymentConfirm === "function") {
                try {
                    onPaymentConfirm(receiptUrls[0], resultingPaymentStatus);
                } catch (err) {
                    console.warn("onPaymentConfirm handler threw:", err);
                }
            }
            onClose();
        } catch (error) {
            console.error("Error processing payment:", error);
            toast({
                title: "Error",
                description:
                    error.message || "Failed to process payment. Please try again.",
                status: "error",
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setIsSubmitting(false);
            setIsBillViewOpen(false)
            onClose();
        }

    };

    // computed values for preview
    const prevReceived = Number(billSummary.RecivedAmt) || 0;
    const displayedReceived = parseFloat((prevReceived + Number(additionalReceived || 0)).toFixed(2));
    const pendingAfter = parseFloat((billSummary.PendingAmt).toFixed(2));

    return (
        <>
            <Modal isOpen={isOpen} onClose={onClose} size="xl">
                <ModalOverlay/>
                <ModalContent>
                    <ModalHeader>
                        {isRefundCase ? "Process Refund" : "Upload Payment Receipt"}
                        {isRefundCase && (
                            <Text fontSize="sm" color="red.500" fontWeight="normal">
                                Refund Amount: ₹{Math.abs(billSummary.PendingAmt)}
                            </Text>
                        )}
                    </ModalHeader>
                    <ModalCloseButton/>
                    <ModalBody>
                        <VStack spacing={4} align="start" w="100%">
                            {/* Bill Summary */}
                            <Box w="100%" p={4} borderWidth="1px" borderColor="gray.400" borderRadius="lg">
                                <Text fontWeight="bold" mb={2}>
                                    Bill Summary
                                </Text>
                                <Flex justify="space-between" mb={2}>
                                    <Text>Total Amount:</Text>
                                    <Text>₹{billSummary.netAmount}</Text>
                                </Flex>
                                <Flex justify="space-between" mb={2}>
                                    <Text>Tax ({billSummary?.taxPercentage ?? billSummary?.tax_percent ?? 10}%)</Text>
                                    <Text>₹{billSummary.taxAmount}</Text>
                                </Flex>
                                <Flex justify="space-between" mb={2} align="center">
                                    <Box>
                                        <Text>Received Amount:</Text>
                                        <Text fontSize="sm" color="gray.600">
                                            Previously: ₹{prevReceived.toFixed(2)}
                                            {"  "}
                                            <Text as="span">Now: ₹{Number(additionalReceived || 0).toFixed(2)}</Text>
                                        </Text>
                                    </Box>
                                    <Text fontWeight="bold">₹{Math.abs(displayedReceived).toFixed(2)}</Text>
                                </Flex>
                                <Divider my={2}/>
                                <Flex
                                    justify="space-between"
                                    fontWeight="bold"
                                    color={
                                        isRefundCase
                                            ? "red.500"
                                            : pendingAfter > 0
                                                ? "orange.500"
                                                : "green.500"
                                    }
                                >
                                    <Text>
                                        {isRefundCase ? "Refund Amount:" : "Pending Amount:"}
                                    </Text>
                                    <Text>₹{Math.abs(pendingAfter).toFixed(2)}</Text>
                                </Flex>
                            </Box>

                            {/* Payment/Refund Confirmation */}
                            {!isRefundCase ? (
                                <Box>
                                    <Text fontSize="sm" color="gray.600" mb={2}>
                                        Have you received the net amount?
                                    </Text>
                                    <Flex gap={3}>
                                        <Button
                                            variant="outline"
                                            onClick={() => {
                                                setBillPaymentNowSelection(true);
                                                setError("");
                                                const net = Number(billSummary.netAmount) || 0;
                                                const prevRec = Number(billSummary.RecivedAmt) || 0;
                                                const pendingBefore = Number(billSummary.PendingAmt) || (net - prevRec);
                                                // set additional to full pending (paying all)
                                                setAdditionalReceived(pendingBefore);
                                                setPaymentState(PaymentStates.PAID);
                                                setispaid(true);
                                                setispending(false);
                                            }}
                                        >
                                            Yes
                                        </Button>
                                        <Button
                                            variant="ghost"
                                            onClick={() => {
                                                setBillPaymentNowSelection(false);
                                                setError("");
                                                setispaid(false);
                                                setispending(true);
                                                setPaymentState(PaymentStates.PENDING);
                                                // user can enter a custom additional amount now; default to 0
                                                setAdditionalReceived(0);
                                            }}
                                        >
                                            No
                                        </Button>
                                    </Flex>
                                </Box>
                            ) : (
                                <Box>
                                    <Text fontSize="sm" color="red.600" mb={2} fontWeight="bold">
                                        Refund Processing Required
                                    </Text>
                                    <Text fontSize="sm" color="gray.600" mb={2}>
                                        This appointment requires a refund of ₹
                                        {Math.abs(billSummary.PendingAmt)}. Please upload the refund
                                        receipt to complete the process.
                                    </Text>
                                </Box>
                            )}

                            {/* Amount Received Input */}
                            {!isRefundCase && (
                                <Box mt={4}>
                                    <FormControl>
                                        <FormLabel fontWeight="600" fontSize="14px">
                                            Amount Received Now (₹)
                                        </FormLabel>
                                        <Text fontSize="sm" color="gray.600" mb={2}>
                                            Previously received: ₹{prevReceived.toFixed(2)}
                                        </Text>
                                        <Input
                                            type="number"
                                            min="0"
                                            step="0.01"
                                            value={additionalReceived}
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                const entered = value === "" ? 0 : parseFloat(value);
                                                const net = Number(billSummary.netAmount) || 0;
                                                const prevRec = Number(billSummary.RecivedAmt) || 0;
                                                const pendingBefore = Number(billSummary.PendingAmt) || (net - prevRec);

                                                if (isNaN(entered) || entered < 0) {
                                                    setError("Invalid received amount");
                                                    return;
                                                }

                                                // Additional received cannot exceed current pending
                                                if (entered > pendingBefore) {
                                                    setError("Amount received now cannot be greater than pending amount");
                                                    return;
                                                }

                                                const pendingAfter = parseFloat((pendingBefore - entered).toFixed(2));
                                                setError("");
                                                setAdditionalReceived(entered);

                                                // update flags based on pending after applying this amount
                                                setispaid(pendingAfter === 0);
                                                setispending(pendingAfter > 0);
                                                setPaymentState(pendingAfter === 0 ? PaymentStates.PAID : PaymentStates.PENDING);
                                            }}
                                            isReadOnly={billPaymentNowSelection === true}
                                        />
                                    </FormControl>

                                    {ispending && (
                                        <FormControl mt={3}>
                                            <FormLabel fontWeight="600" fontSize="14px">
                                                Pending Amount (₹)
                                            </FormLabel>
                                            <Text>₹{pendingAfter.toFixed(2)}</Text>
                                        </FormControl>
                                    )}
                                </Box>
                            )}

                            {/* Refund Amount Display */}
                            {isRefundCase && (
                                <Box mt={4}>
                                    <FormControl>
                                        <FormLabel fontWeight="600" fontSize="14px" color="red.600">
                                            Refund Amount (₹)
                                        </FormLabel>
                                        <Text fontSize="lg" fontWeight="bold" color="red.600">
                                            ₹{Math.abs(billSummary.PendingAmt)}
                                        </Text>
                                    </FormControl>
                                </Box>
                            )}


                            <Text fontWeight="600">
                                Upload {isRefundCase ? "refund" : "payment"} receipt
                            </Text>

                            <Box mt={3}>
                                {receiptItems.length === 0 ? (
                                    <Text fontSize="sm" color="gray.500">
                                        No files uploaded
                                    </Text>
                                ) : (
                                    <Box>
                                        <Text fontSize="sm" mb={2}>
                                            Uploaded files ({receiptItems.length})
                                        </Text>
                                        <Box display="flex" flexWrap="wrap" gap={2}>
                                            {receiptItems.map((item, idx) => (
                                                <Tag
                                                    key={idx}
                                                    borderRadius="full"
                                                    variant="subtle"
                                                    colorScheme="gray"
                                                >
                                                    <TagLabel maxW="200px" isTruncated>
                                                        {item.name || item.url}
                                                    </TagLabel>
                                                    <button
                                                        onClick={() => removeReceiptItem(idx)}
                                                    >
                                                        <div style={{
                                                            fontSize: "1rem",
                                                            color: "red",
                                                            cursor: "pointer",
                                                            marginLeft: "4px"
                                                        }}>X
                                                        </div>
                                                    </button>

                                                </Tag>
                                            ))}
                                        </Box>
                                    </Box>
                                )}
                            </Box>

                            {/* Receipt Upload */}
                            <Box w="100%">
                                <Flex align="left" justify="space-between" flexDir="column" mb={2} gap={10}>
                                    <FileUploadField
                                        id="receipt-file-input"
                                        name="receipt_files"
                                        onChange={async (e) => {
                                            const files = Array.from(e.target.files || []);
                                            if (files.length === 0) return;
                                            await uploadFilesAndSetUrls(files, isRefundCase ? "Refund_Receipt_" : "Payment_Receipt_");
                                            e.target.value = "";
                                        }}
                                        accept=".jpg,.jpeg,.png,.pdf"
                                        inputProps={{multiple: true}}
                                        helperText="You can upload multiple receipts"
                                    />
                                </Flex>


                                {error && (
                                    <Text color="red.500" fontSize="sm" mt={2}>
                                        {error}
                                    </Text>
                                )}
                            </Box>
                        </VStack>
                    </ModalBody>

                    <ModalFooter>
                        <Button
                            variant="solid"
                            colorScheme={isRefundCase ? "red" : "blue"}
                            mr={3}
                            onClick={() => {
                                if (receiptItems.length === 0) {
                                    toast({
                                        title: "No Receipts",
                                        description: "Please upload at least one receipt to view the bill.",
                                        status: "warning",
                                        duration: 3000,
                                        isClosable: true,
                                    })
                                    return
                                }
                                setIsBillViewOpen(true)
                            }}
                        >
                            View Bill
                        </Button>
                        {/* <Button
              colorScheme={isRefundCase ? "red" : "blue"}
              mr={3}
              onClick={handleConfirmPayment}
              isLoading={isSubmitting}
              isDisabled={receiptItems.length === 0 || !!error}
            >
              {isRefundCase ? "Process Refund" : "Confirm Payment"}
            </Button> */}
                        <Button variant="ghost" onClick={onClose}>
                            Cancel
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>



            {/* Bill Preview Modal */}
            <Modal
                isOpen={isBillViewOpen}
                onClose={() => setIsBillViewOpen(false)}
                size="6xl"
            >
                <ModalOverlay/>
                <ModalContent maxW="1000px" maxH="90vh" p={0} bg="white" display="flex" flexDirection="column">
                    <ModalHeader>Bill Preview</ModalHeader>
                    <ModalCloseButton/>

                    <ModalBody
                        overflowY="auto"
                        bg="transparent"
                        p={4}
                        display="flex"
                        justifyContent="center"
                        alignItems="flex-start"
                        flex="1"
                    >
                        {existingBill && (
                            <Bill
                                id="bill-preview"
                                appointmentData={{
                                    patientName: patientName,
                                    patientId: existingBill.patient_id,
                                    doctorId: existingBill.doctor_id,
                                    appointmentDate: existingBill.appointment_date,
                                    appointmentTime: existingBill.appointment_time,
                                }}
                                billSummary={{
                                    ...billSummary,
                                    RecivedAmt: displayedReceived,
                                    PendingAmt: pendingAfter,
                                }}
                                isRefund={isRefundCase}
                                paymentState={
                                    isRefundCase ? PaymentStates.REFUND_DONE : paymentState
                                }
                                billDescription={
                                    isRefundCase
                                        ? `REFUNDED - Amount: ₹${Math.abs(billSummary.PendingAmt)}`
                                        : undefined
                                }
                                RecivedAmt={displayedReceived}
                                pendingAmount={pendingAfter}
                            />
                        )}
                    </ModalBody>

                    <ModalFooter
                        borderTop="1px solid #eee"
                        position="sticky"
                        bottom="0"
                        bg="white"
                        zIndex="1"
                    >
                        <Button
                            variant="outline"
                            mr={3}
                            onClick={() =>
                                generateBillPdf(
                                    "bill-preview",
                                    true,
                                    `${isRefundCase ? "refund" : "payment"}_bill_${appointmentId}.pdf`
                                )
                            }
                        >
                            Download PDF
                        </Button>
                        <Button
                            colorScheme={isRefundCase ? "red" : "blue"}
                            onClick={handleConfirmPayment}
                            isLoading={isSubmitting}
                        >
                            {isRefundCase ? "Process Refund" : "Confirm Payment"}
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>


        </>
    );
};

export default PaymentReceiptModal;
